/**
 * UX Analytics Service - Epic 24 Component Tracking
 * Party Mode Session 3 - UX + Dev
 * 
 * Tracks user interactions with Epic 24 components for data-driven UX optimization
 */

interface AnalyticsEvent {
  eventType: 'component_interaction' | 'feature_usage' | 'milestone_achieved' | 'error_occurred';
  componentName: string;
  action: string;
  metadata?: Record<string, any>;
  timestamp: Date;
  userId?: string;
  sessionId?: string;
}

interface Epic24Metrics {
  // GlobalEmmaFAB Metrics
  emmaFabOpens: number;
  emmaFabCtrlEUsage: number;
  emmaConversationLength: number[];
  emmaMessagesSent: number;
  
  // CommandPalette Metrics
  commandPaletteOpens: number;
  commandPaletteCtrlKUsage: number;
  commandsExecuted: string[];
  searchQueries: string[];
  
  // OnboardingFlow Metrics
  onboardingStarted: number;
  onboardingCompleted: number;
  onboardingSkipped: number;
  stepDropoffRates: Record<string, number>;
  
  // Campaign Creation Metrics
  campaignsCreated: number;
  campaignCompletionTime: number[];
  contentGenerationAttempts: number;
  contentGenerationSuccesses: number;
}

class AnalyticsService {
  private events: AnalyticsEvent[] = [];
  private sessionId: string;
  private metrics: Epic24Metrics;

  constructor() {
    this.sessionId = this.generateSessionId();
    this.metrics = this.initializeMetrics();
    this.loadPersistedMetrics();
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private initializeMetrics(): Epic24Metrics {
    return {
      emmaFabOpens: 0,
      emmaFabCtrlEUsage: 0,
      emmaConversationLength: [],
      emmaMessagesSent: 0,
      commandPaletteOpens: 0,
      commandPaletteCtrlKUsage: 0,
      commandsExecuted: [],
      searchQueries: [],
      onboardingStarted: 0,
      onboardingCompleted: 0,
      onboardingSkipped: 0,
      stepDropoffRates: {},
      campaignsCreated: 0,
      campaignCompletionTime: [],
      contentGenerationAttempts: 0,
      contentGenerationSuccesses: 0,
    };
  }

  private loadPersistedMetrics(): void {
    const stored = localStorage.getItem('cosellus_analytics_metrics');
    if (stored) {
      try {
        this.metrics = JSON.parse(stored);
      } catch (e) {
        console.warn('Failed to load persisted metrics:', e);
      }
    }
  }

  private persistMetrics(): void {
    try {
      localStorage.setItem('cosellus_analytics_metrics', JSON.stringify(this.metrics));
    } catch (e) {
      console.warn('Failed to persist metrics:', e);
    }
  }

  // GlobalEmmaFAB Tracking
  trackEmmaFabOpen(triggeredByCtrlE: boolean = false): void {
    this.metrics.emmaFabOpens++;
    if (triggeredByCtrlE) {
      this.metrics.emmaFabCtrlEUsage++;
    }
    
    this.trackEvent({
      eventType: 'component_interaction',
      componentName: 'GlobalEmmaFAB',
      action: triggeredByCtrlE ? 'open_via_ctrl_e' : 'open_via_click',
      timestamp: new Date(),
    });
    
    this.persistMetrics();
  }

  trackEmmaMessage(messageLength: number): void {
    this.metrics.emmaMessagesSent++;
    this.metrics.emmaConversationLength.push(messageLength);
    
    this.trackEvent({
      eventType: 'feature_usage',
      componentName: 'GlobalEmmaFAB',
      action: 'send_message',
      metadata: { messageLength },
      timestamp: new Date(),
    });
    
    this.persistMetrics();
  }

  // CommandPalette Tracking
  trackCommandPaletteOpen(triggeredByCtrlK: boolean = false): void {
    this.metrics.commandPaletteOpens++;
    if (triggeredByCtrlK) {
      this.metrics.commandPaletteCtrlKUsage++;
    }
    
    this.trackEvent({
      eventType: 'component_interaction',
      componentName: 'CommandPalette',
      action: triggeredByCtrlK ? 'open_via_ctrl_k' : 'open_via_click',
      timestamp: new Date(),
    });
    
    this.persistMetrics();
  }

  trackCommandExecution(commandId: string): void {
    this.metrics.commandsExecuted.push(commandId);
    
    this.trackEvent({
      eventType: 'feature_usage',
      componentName: 'CommandPalette',
      action: 'execute_command',
      metadata: { commandId },
      timestamp: new Date(),
    });
    
    this.persistMetrics();
  }

  trackCommandSearch(query: string): void {
    this.metrics.searchQueries.push(query);
    
    this.trackEvent({
      eventType: 'component_interaction',
      componentName: 'CommandPalette',
      action: 'search',
      metadata: { query },
      timestamp: new Date(),
    });
    
    this.persistMetrics();
  }

  // OnboardingFlow Tracking
  trackOnboardingStart(): void {
    this.metrics.onboardingStarted++;
    
    this.trackEvent({
      eventType: 'milestone_achieved',
      componentName: 'OnboardingFlow',
      action: 'start',
      timestamp: new Date(),
    });
    
    this.persistMetrics();
  }

  trackOnboardingComplete(): void {
    this.metrics.onboardingCompleted++;
    
    this.trackEvent({
      eventType: 'milestone_achieved',
      componentName: 'OnboardingFlow',
      action: 'complete',
      timestamp: new Date(),
    });
    
    this.persistMetrics();
  }

  trackOnboardingSkip(): void {
    this.metrics.onboardingSkipped++;
    
    this.trackEvent({
      eventType: 'component_interaction',
      componentName: 'OnboardingFlow',
      action: 'skip',
      timestamp: new Date(),
    });
    
    this.persistMetrics();
  }

  trackOnboardingStepDropoff(stepName: string): void {
    this.metrics.stepDropoffRates[stepName] = (this.metrics.stepDropoffRates[stepName] || 0) + 1;
    
    this.trackEvent({
      eventType: 'component_interaction',
      componentName: 'OnboardingFlow',
      action: 'step_dropoff',
      metadata: { stepName },
      timestamp: new Date(),
    });
    
    this.persistMetrics();
  }

  // Campaign Creation Tracking
  trackCampaignCreated(completionTimeMs: number): void {
    this.metrics.campaignsCreated++;
    this.metrics.campaignCompletionTime.push(completionTimeMs);
    
    this.trackEvent({
      eventType: 'milestone_achieved',
      componentName: 'CampaignBuilder',
      action: 'campaign_created',
      metadata: { completionTimeMs },
      timestamp: new Date(),
    });
    
    this.persistMetrics();
  }

  trackContentGeneration(success: boolean): void {
    this.metrics.contentGenerationAttempts++;
    if (success) {
      this.metrics.contentGenerationSuccesses++;
    }
    
    this.trackEvent({
      eventType: success ? 'milestone_achieved' : 'error_occurred',
      componentName: 'ContentFlow',
      action: success ? 'generation_success' : 'generation_failure',
      timestamp: new Date(),
    });
    
    this.persistMetrics();
  }

  // Generic Event Tracking
  private trackEvent(event: AnalyticsEvent): void {
    const enrichedEvent = {
      ...event,
      sessionId: this.sessionId,
      userId: this.getUserId(),
    };
    
    this.events.push(enrichedEvent);
    
    // TODO: Send to analytics backend (Mixpanel, PostHog, custom endpoint)
    // this.sendToBackend(enrichedEvent);
    
    // For now, just log to console in development
    if (import.meta.env.DEV) {
      console.log('[Analytics]', enrichedEvent);
    }
  }

  private getUserId(): string | undefined {
    // TODO: Get from authentication context
    return localStorage.getItem('user_id') || undefined;
  }

  // Metrics Reporting
  getMetrics(): Epic24Metrics {
    return { ...this.metrics };
  }

  getMetricsSummary(): Record<string, any> {
    const avgConversationLength = this.metrics.emmaConversationLength.length > 0
      ? this.metrics.emmaConversationLength.reduce((a, b) => a + b, 0) / this.metrics.emmaConversationLength.length
      : 0;
    
    const avgCampaignCompletionTime = this.metrics.campaignCompletionTime.length > 0
      ? this.metrics.campaignCompletionTime.reduce((a, b) => a + b, 0) / this.metrics.campaignCompletionTime.length
      : 0;
    
    const onboardingCompletionRate = this.metrics.onboardingStarted > 0
      ? (this.metrics.onboardingCompleted / this.metrics.onboardingStarted) * 100
      : 0;
    
    const contentGenerationSuccessRate = this.metrics.contentGenerationAttempts > 0
      ? (this.metrics.contentGenerationSuccesses / this.metrics.contentGenerationAttempts) * 100
      : 0;

    const ctrlEUsageRate = this.metrics.emmaFabOpens > 0
      ? (this.metrics.emmaFabCtrlEUsage / this.metrics.emmaFabOpens) * 100
      : 0;

    const ctrlKUsageRate = this.metrics.commandPaletteOpens > 0
      ? (this.metrics.commandPaletteCtrlKUsage / this.metrics.commandPaletteOpens) * 100
      : 0;

    return {
      // Emma FAB Metrics
      totalEmmaInteractions: this.metrics.emmaFabOpens,
      ctrlEAdoptionRate: `${ctrlEUsageRate.toFixed(1)}%`,
      avgConversationLength: avgConversationLength.toFixed(1),
      totalEmmaMessages: this.metrics.emmaMessagesSent,
      
      // Command Palette Metrics
      totalCommandPaletteUses: this.metrics.commandPaletteOpens,
      ctrlKAdoptionRate: `${ctrlKUsageRate.toFixed(1)}%`,
      uniqueCommandsUsed: new Set(this.metrics.commandsExecuted).size,
      totalCommandExecutions: this.metrics.commandsExecuted.length,
      
      // Onboarding Metrics
      onboardingCompletionRate: `${onboardingCompletionRate.toFixed(1)}%`,
      onboardingSkipRate: `${((this.metrics.onboardingSkipped / this.metrics.onboardingStarted) * 100 || 0).toFixed(1)}%`,
      
      // Campaign Metrics
      totalCampaigns: this.metrics.campaignsCreated,
      avgCampaignCompletionTime: `${(avgCampaignCompletionTime / 1000).toFixed(1)}s`,
      contentGenerationSuccessRate: `${contentGenerationSuccessRate.toFixed(1)}%`,
    };
  }

  // Export for reporting
  exportMetrics(): string {
    return JSON.stringify({
      metrics: this.metrics,
      summary: this.getMetricsSummary(),
      events: this.events,
      sessionId: this.sessionId,
      exportedAt: new Date().toISOString(),
    }, null, 2);
  }

  // Reset metrics (for testing)
  resetMetrics(): void {
    this.metrics = this.initializeMetrics();
    this.events = [];
    this.persistMetrics();
  }
}

// Export singleton instance
export const analyticsService = new AnalyticsService();
export type { AnalyticsEvent, Epic24Metrics };
